import React, { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { compressProfileImage } from "@/lib/pendingPhoto";

/**
 * Price — Champ photo de profil optionnel affiché dans les formulaires
 * d'inscription (client + covoitureur).
 *
 * Props :
 *   - value    : data-URL courante (compressée) ou null
 *   - onChange : callback(dataUrl | null)
 *   - testid   : prefix pour les data-testid (bouton + input)
 */
export default function PhotoPickerField({ value, onChange, testid = "photo-picker" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressProfileImage(file);
      onChange?.(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Impossible de charger l'image");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange?.(null);

  return (
    <div className="flex items-center gap-3" data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        data-testid={`${testid}-btn`}
        className="relative w-20 h-20 flex-none rounded-2xl border-2 border-black overflow-hidden bg-[#FFF8F1] grid place-items-center shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all"
      >
        {busy ? (
          <Loader2 className="w-6 h-6 text-neutral-500 animate-spin" strokeWidth={2.5} />
        ) : value ? (
          <img src={value} alt="Photo choisie" className="w-full h-full object-cover" />
        ) : (
          <Camera className="w-8 h-8 text-neutral-400" strokeWidth={2} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-[Outfit] text-[13px] font-black">Photo (optionnelle)</div>
        <div className="mt-0.5 text-[11px] font-medium text-foreground/60 leading-snug">
          Facultative. Tu pourras l&apos;ajouter plus tard depuis le menu.
        </div>
        {value && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-black bg-white px-2 py-0.5 text-[11px] font-black uppercase tracking-wider"
          >
            <X className="w-3 h-3" strokeWidth={3} />
            Retirer
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
